import { FormControl } from '@chakra-ui/form-control';
import {
  FormErrorMessage,
  FormLabel,
  GridItem,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigGeneralSection_ConfigFragment,
  ConfigGeneralSection_CustomerFragment,
  useConfigurationRenameMutation,
} from './__generated__/ConfigGeneralSection.graphql';
import { ConfigInlineGroupsSelect } from './ConfigInlineGroupsSelect';
import { ConfigSiteSelect } from './ConfigSiteSelect';

const schema = z.object({
  name: z.string().transform((v) => v.trim()),
});

type FormValues = z.TypeOf<typeof schema>;

interface Props {
  customer: ConfigGeneralSection_CustomerFragment;
  config: ConfigGeneralSection_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigGeneralSection({ customer, config, configStatus }: Props) {
  // const analytics = useAnalyticsReporter();

  const toast = useToast();
  const { t } = useTranslation();
  const { verifyUserPermissions } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: config.name ?? '',
    },
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  const isUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  const [renameConfig] = useConfigurationRenameMutation();

  const performSubmit = async ({ name }: FormValues) => {
    try {
      await renameConfig({
        variables: {
          input: {
            configurationId: config.id,
            name: isEmpty(name) ? '' : name,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
      // analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'alias' });
    } catch (err) {
      toast({
        status: 'error',
        title: t('renameConfigErr'),
        description: fromError(err, 'UpdateAlias'),
      });
    }
  };

  const handleOnBlur = () => {
    if (!isValid) return;
    handleSubmit(performSubmit)();
  };

  useEffect(() => {
    reset({
      name: config.name ?? '',
    });
  }, [config, reset]);

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      <form onSubmit={handleSubmit(performSubmit)}>
        <FormControl mb="10">
          <FormLabel>{t('nameAlias')}</FormLabel>
          <InputGroup>
            <Input
              {...register('name')}
              isDisabled={isUpdateDisabled || isSubmitting}
              onBlur={handleOnBlur}
            />
            <InputRightElement>{isSubmitting ? <Spinner size="sm" /> : null}</InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>
        <input type="submit" hidden />
      </form>
      <FormControl mb="10">
        <FormLabel>{t('site')}</FormLabel>
        <ConfigSiteSelect customer={customer} config={config} isDisabled={isUpdateDisabled} />
      </FormControl>
      <FormControl>
        <FormLabel>{t('groups')}</FormLabel>
        <ConfigInlineGroupsSelect
          customer={customer}
          config={config}
          isDisabled={isUpdateDisabled}
        />
      </FormControl>
    </GridItem>
  );
}

ConfigGeneralSection.graphql = {
  fragments: {
    ConfigGeneralSection_config: gql`
      fragment ConfigGeneralSection_config on ConfigurationModel {
        id
        name
        ...ConfigSiteSelect_config
        ...ConfigInlineGroupsSelect_config
      }
    `,
    ConfigGeneralSection_customer: gql`
      fragment ConfigGeneralSection_customer on Customer {
        id
        ...ConfigSiteSelect_customer
        ...ConfigInlineGroupsSelect_customer
      }
    `,
  },
  mutations: {
    ConfigurationRename: gql`
      mutation ConfigurationRename($input: ConfigurationRenameInput!) {
        configurationRename(input: $input) {
          id
          name
        }
      }
    `,
  },
};
